import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Carousel, ConfirmPopup, Row } from '~/commonComponents';
import { ICarouselRef } from '~/commonComponents/Carousel/Carousel';
import { useToggle, useUserSelector } from '~/hooks';
import Step01 from '~/images/perm/perm-product-tutorial-01.svg';
import Step02 from '~/images/perm/perm-product-tutorial-02.svg';
import Step03 from '~/images/perm/perm-product-tutorial-03.svg';
import { Colors } from '~/themes/colors';
import { SlideItem } from './SlideItem';
import { IEducationSlideItem } from './Types';

const SLIDES: IEducationSlideItem[] = [
  {
    Illustration: Step01,
    description:
      'Stop wasting time on job posts that don’t bring the right candidates. Let GoTu streamline the process with smarter matches, better visibility, and candidates who fit your practice.',
    title: 'A Better Way to Build Your Team Starts Here',
  },
  {
    Illustration: Step02,
    description:
      'See how candidates blend into your day-to-day. Onsite interviews give you a real look at their skills, communication, and fit, all before you make a final decision.',
    title: 'Onsite Interviews',
  },
  {
    Illustration: Step03,
    description:
      'Post your job, set your onsite interview availability, and connect with candidates who fit your practice.',
    title: 'Ready to Get Started?',
  },
];

export const EducationPopup = () => {
  const currentUser = useUserSelector();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, controls] = useToggle();

  const carouselRef = useRef<ICarouselRef>(null);

  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === SLIDES.length - 1;

  const STORAGE_KEY = useMemo(
    () => `tutorials/permanent-placement/${currentUser.id}`,
    [currentUser.id],
  );

  const getStorage = useCallback(() => {
    if (!currentUser) {
      return;
    }

    const isAppeared = localStorage.getItem(STORAGE_KEY);

    if (isAppeared === null || isAppeared === 'false') {
      controls.setOn();
    }
  }, [STORAGE_KEY, controls, currentUser]);

  const handleClosePopup = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    controls.setOff();
  }, [STORAGE_KEY, controls]);

  useEffect(() => {
    getStorage();
  }, [getStorage]);

  if (!isOpen) {
    return null;
  }

  return (
    // @ts-expect-error - Confirm Popup still written in Javascript
    <ConfirmPopup
      modalStyle={{ maxWidth: 500, maxHeight: 450 } as CSSProperties}
      showClosePopupIcon={false}
    >
      <Carousel
        containerCustomStyle={{ height: 450 }}
        data={SLIDES}
        ref={carouselRef}
        renderItem={SlideItem}
        onSnapToItem={setCurrentIndex}
      />
      <Row marginTop={38} gap={16}>
        {!isFirstStep && (
          <Button
            style={{ width: 152, height: 42, backgroundColor: Colors.secondary_500 }}
            text="Back"
            onClick={() => {
              carouselRef?.current?.goToPrev();
            }}
          />
        )}
        <Button
          style={{ width: 152, height: 42, backgroundColor: Colors.primary_500 }}
          text={isLastStep ? 'Get Started!' : 'Next'}
          onClick={() => {
            if (isLastStep) {
              handleClosePopup();
              return;
            }
            carouselRef?.current?.goToNext();
          }}
        />
      </Row>
    </ConfirmPopup>
  );
};
